import { isBlankCode } from "./codeLensState";

/**
 * Splits Code Lens editor content into a URL-friendly representation.
 *
 * Small snippets ride inside the shared `?state=` blob as `code`. Large
 * snippets would bloat the URL past practical limits, so they are offloaded to
 * `localStorage` and only an opaque `codeRef` key is kept in state — the link
 * still reloads on the author's machine without producing an unusable URL.
 *
 * The outer Memory codec already compresses `?state=`, so we keep raw text here
 * and threshold on raw length.
 */

const LOCAL_STORAGE_PREFIX = "fixparser:code:";
/** Raw-char ceiling for inlining code into the URL state. */
export const URL_CODE_LIMIT = 4_000;

export interface EncodedCodeState {
  code?: string;
  codeRef?: string;
}

const hasLocalStorage = (): boolean => {
  try {
    return typeof localStorage !== "undefined";
  } catch {
    return false;
  }
};

let refCounter = 0;
const makeRef = (): string =>
  `${Date.now().toString(36)}-${(++refCounter).toString(36)}`;

/**
 * Encodes editor content for persistence. Blank/whitespace-only content (and
 * the implicit "use starter code" state) yields `{}` so nothing is written.
 */
export const encodeCodeState = (raw: string | undefined): EncodedCodeState => {
  if (raw === undefined || isBlankCode(raw)) return {};
  if (raw.length <= URL_CODE_LIMIT) return { code: raw };
  if (hasLocalStorage()) {
    const ref = makeRef();
    try {
      localStorage.setItem(LOCAL_STORAGE_PREFIX + ref, raw);
      return { codeRef: ref };
    } catch {
      // Quota/availability failure: fall back to inlining so we lose nothing.
      return { code: raw };
    }
  }
  return { code: raw };
};

/** Inverse of {@link encodeCodeState}; returns undefined when nothing stored. */
export const decodeCodeState = (
  encoded: EncodedCodeState | undefined,
): string | undefined => {
  if (!encoded) return undefined;
  if (encoded.code !== undefined) return encoded.code;
  if (encoded.codeRef !== undefined && hasLocalStorage()) {
    return (
      localStorage.getItem(LOCAL_STORAGE_PREFIX + encoded.codeRef) ?? undefined
    );
  }
  return undefined;
};
